import { Component, Inject, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ComponentAttributes, EventAttributes, LockPolicy, LockPolicyRuleValidation, LockPolicyRun, JobSummary, ComponentTypeExclusion, EventTypeExclusion } from '../../_models/lock-policy';
import { LockPolicyService } from '../../services/lock-policy.service';
import { NotificationService } from '../../services/notification.service';
import { BaseComponent } from '../../shared/base.component';
import { Observable, of, Subject } from 'rxjs';
import { catchError, first, tap, debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import {MatProgressBar} from "@angular/material/progress-bar";
import {
    COMMON_DIALOG_IMPORTS,
    COMMON_FORM_IMPORTS,
    COMMON_TABLE_IMPORTS,
} from "../../shared/import-groups/Common";
import {MatError} from "@angular/material/form-field";
import {MatOptgroup} from "@angular/material/core";
import {RemoveUnderscorePipe} from "../../shared/pipes";
import { CommonModule, DatePipe, TitleCasePipe } from "@angular/common";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatIconButton} from "@angular/material/button";
import {SchedulePickerComponent} from "./schedule-picker/schedule-picker.component";
import {RuleBuilderComponent} from "./rule-builder/rule-builder.component";
import {TypeExclusionsPanelComponent} from "./type-exclusions-panel/type-exclusions-panel.component";

export interface LockPolicyDialogData {
    lock_policy_ids: string[];
}

@Component({
    selector: 'lock-policy-form-dialog',
    templateUrl: './lock-policy-form-dialog.component.html',
    styleUrls: ['./lock-policy-form-dialog.component.less'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0', overflow: 'hidden'})),
            state('expanded', style({height: '*', overflow: 'visible'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [
        MatProgressBar,
        CommonModule,
        COMMON_DIALOG_IMPORTS,
        COMMON_FORM_IMPORTS,
        MatError,
        MatOptgroup,
        RemoveUnderscorePipe,
        TitleCasePipe,
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatIcon,
        MatCardContent,
        MatProgressSpinner,
        COMMON_TABLE_IMPORTS,
        MatIconButton,
        DatePipe,
        SchedulePickerComponent,
        RuleBuilderComponent,
        TypeExclusionsPanelComponent
    ]
})
export class LockPolicyFormDialogComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    policy: LockPolicy;
    form_title = '';
    disabled = false;
    policyLoading = false;

    validationResult: LockPolicyRuleValidation | null = null;
    validationLoading = false;
    validationError = false;
    private ruleChange$ = new Subject<any>();
    private destroy$ = new Subject<void>();
    private confirmSnackbarRef: any = null;

    get canSave(): boolean {
        return !this.validationLoading &&
               !this.validationError &&
               !this.validationResult?.contradiction &&
               this.policy?.attributes?.rule !== null &&
               this.policy?.attributes?.rule !== undefined &&
               this.isRuleComplete(this.policy.attributes.rule);
    }

    // Group attributes by type to avoid duplicates and provide better organization
    attributeGroups: { label: string; attributes: string[] }[] = [];
    selectedAttributes: string[] = [];
    cpFilters: any[] = [];
    cpExclusionsDisabled = true;
    cpExclusionsTooltip = 'Configure a rule with component or event types to enable CP exclusions';
    availableCPIds: string[] = [];

    get componentTypeExclusions(): ComponentTypeExclusion[] {
        return this.policy?.attributes?.exclusions
            ?.filter(e => 'component_type_id' in e) as ComponentTypeExclusion[] || [];
    }

    get eventTypeExclusions(): EventTypeExclusion[] {
        return this.policy?.attributes?.exclusions
            ?.filter(e => 'event_type_id' in e) as EventTypeExclusion[] || [];
    }

    runs: LockPolicyRun[] = [];
    runsLoading = false;
    runsError: string | null = null;

    // Pagination properties
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    totalRuns = 0;
    pageSize = 20;
    currentPage = 0;
    displayedColumns = ['expand', 'start_time', 'end_time', 'components', 'events', 'cps', 'jobs_created', 'component_types', 'event_types'];

    // Store current sort state to maintain it across data loads
    currentSortActive = 'start_time';
    currentSortDirection: 'asc' | 'desc' | '' = 'desc';

    // Job expansion properties
    expandedRun: LockPolicyRun | null = null;
    runJobs: { [runId: string]: JobSummary[] } = {};
    loadingJobs: { [runId: string]: boolean } = {};

    constructor(
        private dialogRef: MatDialogRef<LockPolicyFormDialogComponent>,
        private notification: NotificationService,
        private policyService: LockPolicyService,
        @Inject(MAT_DIALOG_DATA) private dialogData: LockPolicyDialogData
    ) {
        super();
    }

    ngOnInit() {
        this.initializeAttributeGroups();

        if (!this.dialogData.lock_policy_ids) {
            this.policy = new LockPolicy();
            this.form_title = 'Add Lock Policy';
            this.selectedAttributes = [...this.policy.attributes.attributes_to_lock];
            this.onRuleChanged(this.policy.attributes.rule);
        } else {
            this.getPolicy();
        }

        // Set up real-time validation with 1 second debounce to reduce update frequency
        this.ruleChange$.pipe(
            debounceTime(1000),
            switchMap(rule => {
                if (!rule) {
                    this.validationLoading = false;
                    this.validationError = false;
                    this.validationResult = null;
                    this.updateCPFiltersFromValidation();
                    return of(null);
                }

                // Filter out incomplete rules before validation
                const filteredRule = this.filterIncompleteRules(rule);

                // If no complete rules remain after filtering, clear validation
                if (!filteredRule) {
                    this.validationLoading = false;
                    this.validationError = false;
                    this.validationResult = null;
                    this.updateCPFiltersFromValidation();
                    return of(null);
                }

                this.validationLoading = true;
                this.validationError = false;
                return this.policyService.validateRule(
                    filteredRule,
                    this.policy?.attributes?.exclusions,
                    this.policy?.attributes?.attributes_to_lock
                ).pipe(
                    catchError(() => {
                        this.validationLoading = false;
                        this.validationError = true;
                        return of(null);
                    })
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe((validation: LockPolicyRuleValidation | null) => {
            this.validationLoading = false;
            this.validationResult = validation;
            // Update CP filters based on validation results
            this.updateCPFiltersFromValidation();
        });

        // Validate immediately if editing existing policy
        if (this.policy?.attributes?.rule) {
            this.ruleChange$.next(this.policy.attributes.rule);
        }
    }



    onComponentTypeExclusionsChange(exclusions: ComponentTypeExclusion[]) {
        // Remove all existing component type exclusions
        const eventExclusions = this.eventTypeExclusions;
        this.policy.attributes.exclusions = [...exclusions, ...eventExclusions];

        // Trigger revalidation with new exclusions
        if (this.policy.attributes.rule) {
            this.ruleChange$.next(this.policy.attributes.rule);
        }
    }

    onEventTypeExclusionsChange(exclusions: EventTypeExclusion[]) {
        // Remove all existing event type exclusions
        const componentExclusions = this.componentTypeExclusions;
        this.policy.attributes.exclusions = [...componentExclusions, ...exclusions];

        // Trigger revalidation with new exclusions
        if (this.policy.attributes.rule) {
            this.ruleChange$.next(this.policy.attributes.rule);
        }
    }

    getPolicy() {
        this.policyLoading = true;
        this.form_title = 'Loading...';
        this.policyService.getPolicy(this.dialogData.lock_policy_ids[0]).pipe(
            tap(result => {
                this.policy = result;
                this.form_title = 'Edit Lock Policy';
                this.selectedAttributes = [...this.policy.attributes.attributes_to_lock];
                // Exclusions will be handled by the type-exclusion-selector components
                // Trigger validation for existing policy (which will update CP filters)
                if (this.policy.attributes.rule) {
                    this.ruleChange$.next(this.policy.attributes.rule);
                }
                this.policyLoading = false;
            }),
            catchError(() => {
                this.policyLoading = false;
                this.notification.openError('Failed to load policy', 5000);
                this.dialogRef.close();
                return of(null);
            }),
            first()
        ).subscribe();
    }

    onRuleChanged(rule?: any) {
        this.validationResult = null;
        if (rule) {
            this.policy.attributes.rule = rule;
        }
        if (this.confirmSnackbarRef) {
            this.confirmSnackbarRef.dismiss();
            this.confirmSnackbarRef = null;
        }

        // Emit rule change for real-time validation (which will update CP filters)
        this.ruleChange$.next(rule);
    }

    private updateCPFiltersFromValidation() {
        if (!this.policy?.attributes?.rule) {
            this.cpFilters = [];
            this.availableCPIds = [];
            this.cpExclusionsDisabled = true;
            this.cpExclusionsTooltip = 'Configure a rule to enable CP exclusions';
            return;
        }

        // Check if rule has contradiction
        if (this.validationResult?.contradiction) {
            this.cpFilters = [];
            this.availableCPIds = [];
            this.cpExclusionsDisabled = true;
            this.cpExclusionsTooltip = 'Fix rule contradictions before configuring CP exclusions';
            return;
        }

        // Check if we have validation results with type IDs
        if (!this.validationResult?.affected) {
            this.cpFilters = [];
            this.availableCPIds = [];
            this.cpExclusionsDisabled = true;
            this.cpExclusionsTooltip = 'Validating rule...';
            return;
        }

        const componentTypeIds = this.validationResult.affected.component_type_ids || [];
        const eventTypeIds = this.validationResult.affected.event_type_ids || [];
        const cpIds = this.validationResult.affected.cp_ids || [];

        if (componentTypeIds.length === 0 && eventTypeIds.length === 0) {
            // No types matched by rule
            this.cpFilters = [];
            this.availableCPIds = [];
            this.cpExclusionsDisabled = true;
            this.cpExclusionsTooltip = 'No component or event types match this rule';
            return;
        }

        // Enable CP exclusions
        this.cpExclusionsDisabled = false;
        this.cpExclusionsTooltip = '';

        // Update available CP IDs for rule builder
        this.availableCPIds = cpIds;

        // Use cp_ids to filter the available CPs
        if (cpIds.length > 0) {
            this.cpFilters = [{
                name: 'id',
                op: 'in',
                val: cpIds
            }];
        } else {
            // No CPs available
            this.cpFilters = [];
            this.cpExclusionsDisabled = true;
            this.cpExclusionsTooltip = 'No constant properties available for the matched types';
        }
    }

    save() {
        // Check if we can save
        if (!this.canSave) {
            return;
        }

        this.policy.attributes.attributes_to_lock = this.selectedAttributes;

        // Exclusions are already maintained in the policy.attributes.exclusions array
        // by the onComponentTypeExclusionsChange and onEventTypeExclusionsChange methods

        if (!this.policy.attributes.name || this.policy.attributes.name.trim() === '') {
            this.notification.openError('Name is required.', 5000);
            return;
        }

        // Show confirmation with affected models description
        this.confirmSnackbarRef = this.notification.openConfirm(this.getAffectedDescription(), 'Cancel', 'Proceed');
        this.confirmSnackbarRef.onAction().pipe(first()).subscribe(() => {
            let $save: Observable<LockPolicy>;
            if (this.policy.id) {
                $save = this.policyService.updatePolicy(this.policy.id, this.policy.attributes);
            } else {
                $save = this.policyService.createPolicy(this.policy.attributes);
            }
            $save.pipe(
                tap(() => {
                    this.notification.openSuccess('Policy saved', 3000);
                    this.dialogRef.close(this.policy);
                    this.confirmSnackbarRef = null;
                }),
                catchError(e => {
                    this.notification.openError('Unable to save policy', 10000);
                    this.confirmSnackbarRef = null;
                    return of(e);
                }),
                first(),
            ).subscribe();
        }, () => {
            this.confirmSnackbarRef = null;
        });
    }

    onCloseClick(): void {
        this.dialogRef.close();
    }

    ngAfterViewInit() {
        // ViewChild references are available here
        // However, MatSort might not be available immediately if the table is in a hidden tab
        // The sort initialization is handled by the template attributes:
        // matSortActive="start_time" matSortDirection="desc"
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.ruleChange$.complete();
    }

    private initializeAttributeGroups() {
        // Create grouped structure
        this.attributeGroups = [
            {
                label: 'Component Attributes',
                attributes: [...ComponentAttributes]
            },
            {
                label: 'Event Attributes',
                attributes: [...EventAttributes]
            }
        ];
    }

    getAffectedDescription(): string {
        if (!this.validationResult) {
            return '';
        }
        const affected = this.validationResult.affected;
        const ct = affected.component_type_ids?.length || 0;
        const et = affected.event_type_ids?.length || 0;
        const comp = affected.component_count || 0;
        const ev = affected.event_count || 0;
        const cp = affected.cp_count || 0;
        const templateCount = ct + et;
        return `Policy will create or update ${templateCount} Lock Template${templateCount === 1 ? '' : 's'}, which will lock ${comp} Component${comp === 1 ? '' : 's'}, ${ev} Event${ev === 1 ? '' : 's'}, and ${cp} Constant Propert${cp === 1 ? 'y' : 'ies'}`;
    }

    getContradiction(): LockPolicyRuleValidation['contradiction'] {
        if (!this.validationResult) {
            return undefined;
        }
        return this.validationResult.contradiction;
    }

    private isRuleComplete(rule: any): boolean {
        if (!rule || !rule.type) {
            return false;
        }

        switch (rule.type) {
            case 'cp_value':
                // CP value rule needs cp_id, operator, and value
                return !!(rule.cp_id && rule.operator && rule.value !== undefined && rule.value !== null);

            case 'component_type':
                // Component type rule needs component_type_id
                return !!(rule.component_type_id && rule.operator);

            case 'event_type':
                // Event type rule needs event_type_id
                return !!(rule.event_type_id && rule.operator);

            case 'attribute_value':
            case 'component_attribute_value':
            case 'event_attribute_value':
                // Attribute rules need attribute, operator, and value
                return !!(rule.attribute && rule.operator && rule.value !== undefined && rule.value !== null);

            case 'compound':
                // Compound rule is complete if it has rules and all sub-rules are complete
                if (!rule.rules || !Array.isArray(rule.rules) || rule.rules.length === 0) {
                    return false;
                }
                // All sub-rules must be complete
                return rule.rules.every(subRule => this.isRuleComplete(subRule));

            default:
                return false;
        }
    }

    /**
     * Filters out incomplete rules from a rule tree without mutating the original.
     * Returns null if the rule itself is incomplete or if all subrules are filtered out.
     * @param rule The rule to filter
     * @returns A filtered copy of the rule or null if the rule should be excluded
     */
    private filterIncompleteRules(rule: any): any | null {
        if (!rule || !rule.type) {
            return null;
        }

        // Check if this rule is incomplete based on its type
        switch (rule.type) {
            case 'cp_value':
                // CP value rule needs cp_id, operator, and value
                if (!rule.cp_id || !rule.operator || rule.value === undefined || rule.value === null) {
                    return null;
                }
                // Return a shallow copy
                return { ...rule };

            case 'component_type':
                // Component type rule needs component_type_id and operator
                if (!rule.component_type_id || !rule.operator) {
                    return null;
                }
                return { ...rule };

            case 'event_type':
                // Event type rule needs event_type_id and operator
                if (!rule.event_type_id || !rule.operator) {
                    return null;
                }
                return { ...rule };

            case 'attribute_value':
            case 'component_attribute_value':
            case 'event_attribute_value':
                // Attribute rules need attribute, operator, and value
                if (!rule.attribute || !rule.operator || rule.value === undefined || rule.value === null) {
                    return null;
                }
                return { ...rule };

            case 'compound':
                // For compound rules, filter subrules recursively
                if (!rule.rules || !Array.isArray(rule.rules) || rule.rules.length === 0) {
                    return null;
                }

                // Filter subrules, removing incomplete ones
                const filteredSubrules = rule.rules
                    .map(subRule => this.filterIncompleteRules(subRule))
                    .filter(subRule => subRule !== null);

                // If all subrules were filtered out, return null
                if (filteredSubrules.length === 0) {
                    return null;
                }

                // Return a new compound rule with filtered subrules
                return {
                    ...rule,
                    rules: filteredSubrules
                };

            default:
                // Unknown rule type, consider it incomplete
                return null;
        }
    }

    onAttributesToLockChange(attrs: string[]) {
        this.selectedAttributes = attrs;
        this.policy.attributes.attributes_to_lock = attrs;

        // Trigger revalidation with new attributes
        if (this.policy.attributes.rule) {
            this.ruleChange$.next(this.policy.attributes.rule);
        }
    }

    onTabChange(event: any) {
        if (event.index === 1) {
            // Reset pagination when switching to run history tab
            this.currentPage = 0;

            // Load the runs for the first time when switching to this tab
            // Use setTimeout to ensure the view is rendered and MatSort is available
            setTimeout(() => {
                this.loadPolicyRuns();
            }, 0);
        }
    }

    loadPolicyRuns() {
        if (!this.policy?.id) {
            this.runs = [];
            return;
        }
        this.runsLoading = true;
        this.runsError = null;

        // Construct the sort parameter from stored state or MatSort state
        // Backend expects '-field' for descending, 'field' for ascending
        let sortParam = '';

        // Use stored sort state which is maintained across data loads
        if (this.currentSortActive && this.currentSortDirection) {
            if (this.currentSortDirection === 'desc') {
                sortParam = `-${this.currentSortActive}`;
            } else if (this.currentSortDirection === 'asc') {
                sortParam = this.currentSortActive;
            }
        }

        const params: any = {
            page: this.currentPage + 1, // API uses 1-based pagination
            page_size: this.pageSize
        };

        // Only include sort parameter if it's not empty
        if (sortParam) {
            params.sort = sortParam;
        }

        this.policyService.getPolicyRuns(this.policy.id, params)
            .pipe(first())
            .subscribe({
                next: (result) => {
                    this.runs = result.runs;
                    this.totalRuns = result.meta?.count || 0;
                    this.runsLoading = false;
                },
                error: (err) => {
                    this.runsError = 'Failed to load run history.';
                    this.runsLoading = false;
                }
            });
    }

    onPageChange(event: PageEvent) {
        this.currentPage = event.pageIndex;
        this.pageSize = event.pageSize;
        console.log('onPageChange', event);
        this.loadPolicyRuns();
    }

    onSortChange(event: Sort) {
        console.log('onSortChange', event);

        // Update stored sort state
        this.currentSortActive = event.active;
        this.currentSortDirection = event.direction;

        // Reset to first page when sort changes
        this.currentPage = 0;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }

        // Load data with new sort
        this.loadPolicyRuns();
    }

    toggleRunExpansion(run: LockPolicyRun) {
        if (this.expandedRun?.id === run.id) {
            this.expandedRun = null;
        } else {
            this.expandedRun = run;
            if (!this.runJobs[run.id] && run.data?.job_ids?.length > 0) {
                this.loadRunJobs(run);
            }
        }
    }

    loadRunJobs(run: LockPolicyRun) {
        this.loadingJobs[run.id] = true;
        this.policyService.getPolicyRunJobs(this.policy.id, run.id)
            .pipe(first())
            .subscribe({
                next: (jobs) => {
                    this.runJobs[run.id] = jobs;
                    this.loadingJobs[run.id] = false;
                },
                error: () => {
                    this.loadingJobs[run.id] = false;
                }
            });
    }
}
